import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form as FormMik } from 'formik';
import { Button, Form } from 'react-bootstrap';
import * as Yup from 'yup';
import { BsFillEyeFill, BsFillEyeSlashFill } from 'react-icons/bs';
import { useToast } from '../hooks/toast';
import { useAuth } from '../hooks/auth';

import api from '../services/apiClient';

const initialValues = {
  seqUsuario: 0,
  usuario: '',
  nome: '',
  email: '',
  changePassword: true,
  password: '',
  confirmPassword: '',
  ativo: true,
  administrador: false,
};

interface User {
  seqUsuario: number;
  usuario: string;
  nome: string;
  email: string;
  changePassword?: boolean;
  password: string;
  confirmPassword?: string;
  ativo: string;
  administrador: string;
}

interface UserValues extends Omit<User, 'ativo' | 'administrador'> {
  ativo: boolean;
  administrador: boolean;
}

interface Props {
  userSelected: User;
  handlerSave: (dataUser: User) => void;
  handleChangeNameUser: (seqUsuario: number, nome: string) => void;
  userColor: string;
}

const estiloBotao = {
  background: '#fff',
  borderColor: '#007bff',
  borderRadius: '10px',
  color: '#6a6969',
  top: 0,
  right: 0,
  padding: '0 0rem',
  margin: '0 2.2rem',
};

const UserAdd: React.FC<any> = ({
  userSelected,
  handlerSave,
  handleChangeNameUser,
  userColor,
}: Props) => {
  const { addToast } = useToast();
  const { setCorUser } = useAuth();
  const [dataLoad, setDataLoad] = useState<UserValues>({} as UserValues);

  const inputName = useRef<HTMLInputElement>(null);
  const bntCadastrar = useRef<HTMLButtonElement>(null);
  const inputPassword = useRef<HTMLInputElement>(null);
  const inputConfirmPassword = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const token = localStorage.getItem('@LiveChat:token');
  const administrador = localStorage.getItem('@LiveChat:administrador') === 'S';

  useEffect(() => {
    setDataLoad({
      ...userSelected,
      ativo: userSelected.ativo === 'S',
      administrador: userSelected.administrador === 'S',
      confirmPassword: '',
    });

    if (inputName.current) {
      inputName.current.focus();
    }
  }, [userSelected]);

  const validationSchemaUser = Yup.object().shape({
    usuario: Yup.string()
      .required('Usuario é obrigatório')
      .max(30, 'Usuario deve ter no máximo 30 caracteres'),
    nome: Yup.string()
      .required('Nome é obrigatório')
      .max(70, 'Nome deve ter no máximo 70 caracteres'),
    email: Yup.string()
      .email('Email inválido')
      .max(100, 'Email deve ter no máximo 100 caracteres'),
    password: Yup.string()
      .when('changePassword', (changePassword, schema) =>
        changePassword ? schema.required('Senha é obrigatória') : schema
      )
      .min(6, 'Senha deve ter no mínimo 6 caracteres')
      .max(30, 'Senha deve ter no máximo 30 caracteres'),
    confirmPassword: Yup.string()
      .when('changePassword', (changePassword, schema) =>
        changePassword ? schema.required('Senha é obrigatória') : schema
      )
      .max(30, 'Confirmação de senha deve ter no máximo 30 caracteres')
      .when('changePassword', (changePassword, schema) =>
        changePassword
          ? schema
              .required('Senhas não conferem')
              .oneOf([Yup.ref('password'), null], 'Senhas não conferem')
          : schema
      ),
  });

  const handleSubmit = (values: UserValues): void => {
    const data = {
      seqUsuario: values.seqUsuario,
      usuario: values.usuario,
      nome: values.nome,
      email: values.email,
      password: values.password,
      ativo: values.ativo ? 'S' : 'N',
      administrador: values.administrador ? 'S' : 'N',
      cor: userColor,
    };

    if (dataLoad.seqUsuario <= 0) {
      api
        .post('/api/user', data, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          const result = response.data;
          if (result.success) {
            addToast({
              title: 'Sucesso',
              description: 'Cadastrado com sucesso',
              type: 'success',
            });
            setDataLoad(result.data);
            handlerSave(result.data);
          } else {
            const checkValues = values;
            checkValues.usuario = '';
            setDataLoad(checkValues);
            addToast({ title: result.message, type: 'error' });
          }
        })
        .catch((error) => {
          console.log('error', error);
        });
    } else {
      api
        .put('/api/user', data, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          const result = response.data;
          if (result.success) {
            addToast({
              title: 'Sucesso',
              description: 'Atualizado com sucesso',
              type: 'success',
            });

            const dataSaved = {
              seqUsuario: result.data.seqUsuario,
              usuario: result.data.usuario,
              nome: result.data.nome,
              email: result.data.email,
              password: '',
              confirmPassword: '',
              ativo: result.data.ativo === 'S',
              administrador: result.data.administrador === 'S',
              changePassword: false,
            };

            setDataLoad(dataSaved);

            // Se o usuário alterado for o usuário logado, atualiza os dados na tela
            const seqUsuario = localStorage.getItem('@LiveChat:seqUsuario');
            if (seqUsuario === dataSaved.seqUsuario.toString()) {
              setCorUser(result.data.cor);
              localStorage.setItem('@LiveChat:corUser', result.data.cor);
            }

            handlerSave(result.data);
          }
        })
        .catch((error) => {
          console.log('error', error);
        });
    }
  };

  return (
    <section className="m-2">
      <Formik
        initialValues={dataLoad || initialValues}
        validationSchema={validationSchemaUser}
        enableReinitialize
        onSubmit={(values, { setSubmitting, resetForm }) => {
          setSubmitting(true);
          handleSubmit(values);
          resetForm();
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
          resetForm,
        }) => (
          <FormMik>
            <Form.Group controlId="nome" className="mb-2">
              <Form.Label>Nome</Form.Label>
              <Form.Control
                type="text"
                placeholder="Nome do usuário"
                name="nome"
                onChange={(e) => {
                  handleChange(e);
                  handleChangeNameUser(userSelected.seqUsuario, e.target.value);
                }}
                ref={inputName}
                onBlur={handleBlur}
                disabled={!userSelected || !userSelected.seqUsuario}
                value={values.nome}
                className={`inputBox ${
                  touched.nome && errors.nome ? 'is-invalid' : ''
                }`}
              />
              {touched.nome && errors.nome ? (
                <div className="invalid-feedback">{errors.nome}</div>
              ) : null}
            </Form.Group>

            <Form.Group controlId="email" className="mb-2">
              <Form.Label>E-mail</Form.Label>
              <Form.Control
                type="text"
                placeholder="E-mail do usuário"
                name="email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                disabled={!userSelected || !userSelected.seqUsuario}
                className={`inputBox ${
                  touched.email && errors.email ? 'is-invalid' : ''
                }`}
              />
              {touched.email && errors.email ? (
                <div className="invalid-feedback">{errors.email}</div>
              ) : null}
            </Form.Group>
            <Form.Group controlId="usuario" className="mb-2">
              <Form.Label>Login</Form.Label>
              <Form.Control
                type="text"
                placeholder="Usuário"
                name="usuario"
                autoComplete="username"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.usuario}
                className={`text-uppercase inputBox ${
                  touched.usuario && errors.usuario ? 'is-invalid' : ''
                }`}
              />
              {touched.usuario && errors.usuario ? (
                <div className="invalid-feedback">{errors.usuario}</div>
              ) : null}
            </Form.Group>

            <Form.Group
              controlId="password"
              className="mb-2 d-flex justify-content-between"
            >
              <div className="w-100 me-2">
                <Form.Label>Senha</Form.Label>
                <div className="d-flex position-relative">
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Senha"
                    name="password"
                    autoComplete="new-password"
                    ref={inputPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                    disabled={!userSelected || !userSelected.seqUsuario}
                    className={`inputBox ${
                      touched.password && errors.password ? 'is-invalid' : ''
                    }`}
                  />
                  <Button
                    className="border-0 shadow-none position-absolute"
                    variant=""
                    style={estiloBotao}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <BsFillEyeSlashFill /> : <BsFillEyeFill />}
                  </Button>
                </div>

                {touched.password && errors.password ? (
                  <div className="invalid-feedback">{errors.password}</div>
                ) : null}
              </div>
              <div className="w-100 ms-2">
                <Form.Label>Confirmar Senha</Form.Label>
                <div className="d-flex position-relative">
                  <Form.Control
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirmar senha"
                    name="confirmPassword"
                    ref={inputConfirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.confirmPassword}
                    disabled={!userSelected || !userSelected.seqUsuario}
                    className={`inputBox ${
                      touched.confirmPassword && errors.confirmPassword
                        ? 'is-invalid'
                        : ''
                    }`}
                  />
                  <Button
                    className="border-0 shadow-none position-absolute"
                    variant=""
                    style={estiloBotao}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <BsFillEyeSlashFill />
                    ) : (
                      <BsFillEyeFill />
                    )}
                  </Button>
                </div>
                {touched.confirmPassword && errors.confirmPassword ? (
                  <div className="invalid-feedback">
                    {errors.confirmPassword}
                  </div>
                ) : null}
              </div>
            </Form.Group>

            <Form.Check
              type="checkbox"
              name="administrador"
              label="Administrador"
              checked={values.administrador}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={
                !administrador || !userSelected || !userSelected.seqUsuario
              }
              className="input-CheckBox mt-3"
            />

            <Form.Check
              type="checkbox"
              name="ativo"
              label="Ativo"
              checked={values.ativo}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={
                !administrador || !userSelected || !userSelected.seqUsuario
              }
              className="input-CheckBox mt-2"
            />

            <div className="d-flex justify-content-center align-items-center mt-4">
              <Button
                variant="secondary"
                type="button"
                className="mt-2 me-1"
                style={{ fontSize: '1.25rem', fontWeight: '600' }}
                hidden={!administrador}
                disabled={
                  isSubmitting || !userSelected || !userSelected.seqUsuario
                }
                onClick={() => {
                  setDataLoad({
                    ...initialValues,
                  } as UserValues);

                  resetForm();
                }}
              >
                limpar dados
              </Button>
              <Button
                variant="primary"
                type="submit"
                className="mt-2 ms-1"
                ref={bntCadastrar}
                disabled={
                  isSubmitting || !userSelected || !userSelected.seqUsuario
                }
                style={{ fontSize: '1.25rem', fontWeight: '600' }}
              >
                confirmar
              </Button>
            </div>
          </FormMik>
        )}
      </Formik>
    </section>
  );
};

export default UserAdd;
